import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Material } from "../../features/materials/models/Material";
import { Group } from "../../features/members/models/Group";
import { Member } from "../../features/members/models/Member";
import MaterialAudiencePicker, { MaterialAudience } from "./MaterialAudiencePicker";

interface MaterialEditDialogProps {
  material: Material;
  categories: string[];
  members: Member[];
  groups: Group[];
  busy: boolean;
  onClose: () => void;
  onSubmit: (category: string, description: string, audience: MaterialAudience) => void;
}

/** Edit a material's category, description, and audience (the file itself stays as uploaded). */
const MaterialEditDialog: React.FC<MaterialEditDialogProps> = ({
  material,
  categories,
  members,
  groups,
  busy,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [category, setCategory] = useState(material.category);
  const [description, setDescription] = useState(material.description ?? "");
  const [audience, setAudience] = useState<MaterialAudience>({
    everyone: material.memberIds.length === 0 && material.groupIds.length === 0,
    memberIds: material.memberIds,
    groupIds: material.groupIds,
  });

  const audienceValid =
    audience.everyone || audience.memberIds.length > 0 || audience.groupIds.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 dark:bg-black/70">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-4 shadow-xl lg:p-6 dark:bg-primary-10">
        <h3 className="text-grey-10 dark:text-grey-90">
          {t("materials.editTitle", "Edit material")}
        </h3>
        <p className="mt-1 truncate text-sm text-grey-30 dark:text-grey-70">{material.fileName}</p>

        <div className="mt-4 space-y-3">
          <div>
            <label htmlFor="material-category" className="mb-1 block text-sm text-grey-20 dark:text-grey-80">
              {t("materials.categoryLabel", "Category")}
            </label>
            <input
              id="material-category"
              type="text"
              className="input"
              value={category}
              maxLength={100}
              list="material-category-options"
              onChange={(e) => setCategory(e.target.value)}
              disabled={busy}
            />
            <datalist id="material-category-options">
              {categories.map((option) => (
                <option key={option} value={option} />
              ))}
            </datalist>
          </div>
          <div>
            <label htmlFor="material-description" className="mb-1 block text-sm text-grey-20 dark:text-grey-80">
              {t("materials.descriptionLabel", "Description (optional)")}
            </label>
            <input
              id="material-description"
              type="text"
              className="input"
              value={description}
              maxLength={500}
              onChange={(e) => setDescription(e.target.value)}
              disabled={busy}
            />
          </div>

          <MaterialAudiencePicker
            members={members}
            groups={groups}
            value={audience}
            disabled={busy}
            onChange={setAudience}
          />
        </div>

        <div className="mt-5 flex justify-end gap-3">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
            {t("common.cancel", "Cancel")}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => onSubmit(category, description, audience)}
            disabled={busy || category.trim() === "" || !audienceValid}
          >
            {t("common.save", "Save")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MaterialEditDialog;
